import styles from "~/sass/organisms/Article/Article.module.scss";
import Head from "~/components/Meta/Head";
import ArticleAuthor from "~/components/molecules/Article/ArticleAuthor.js";
import Script from "next/script";

export default function HelpingHomelessArticle({}) {
  const content = () => {
    return (
      <>
        <h1 className={styles["Article__heading"]}>
          Helping the Homeless: Ways to Get Involved
        </h1>
        <ArticleAuthor />
        <p>
          Homelessness has become an ever-increasing problem for people of all
          ages, genders, and ethnicities. The high cost of housing, stagnant
          wages, mental illness, and domestic violence are just some of the
          reasons why a person might find themselves homeless. When a person is
          without a home, they face many struggles, such as finding food,
          keeping warm in the winter, and locating a safe place to sleep at
          night. To ease that struggle, there are many ways that people can help
          care for the homeless by using the acronym CARE.
        </p>
        <h2 className={styles["Article__sub-heading"]}>C: Contribute</h2>
        <p>
          Helping the homeless isn't a simple task. It takes organization,
          people, and supplies to provide necessary services to people without
          homes. One of the most basic yet effective ways for people to help
          combat homelessness is to contribute things for people without homes.
        </p>
        <ul>
          <li>
            <b>Donate clothing.</b> Clothes fulfill a basic need by protecting
            people from the elements, preserving their modesty, and helping them
            to maintain a sense of dignity. A lack of decent, clean clothes can
            bar people from activities that may improve their circumstances,
            such as finding employment. Before donating clothing, check with
            homeless shelters or other organizations that help the poor and
            homeless to determine what they need for the current time of year.
            Because the people who will receive the clothes may have a limited
            ability to launder them, always donate items that are clean.
          </li>
          <li>
            <b>Donate books. </b>Books offer a source of entertainment that's
            portable and doesn't take up much space. Donate books for shelters
            to lend out or give away.
          </li>
          <li>
            <b>Create and distribute shelter cards. </b>Write the names and
            locations of area shelters on cards and pass them out to homeless
            individuals who may not know what their shelter options are.
          </li>
          <li>
            <b>Organize a winter drive. </b>The need for outerwear increases
            significantly during the colder months. People can organize winter
            drives with their place of work or church to collect coats,
            blankets, and other gear.
          </li>
          <li>
            <b>Donate used electronics.</b> Nonprofit organizations rarely have
            the funds for expensive equipment that's needed for day-to-day
            operations. People can help by donating their used computers,
            printers, and/or other office machinery.
          </li>
          <li>
            <b>Make survival kits.</b> Enlist the help of friends, church
            members, or coworkers to create and distribute survival kits. Fill
            each bag with essential items such as toothbrushes, toothpaste,
            bottled water, soap, and socks. Kits for women should also include
            feminine hygiene supplies.
          </li>
        </ul>
        <p>
          Other ways that one may consider contributing to the homeless include:
        </p>
        <ul>
          <li>
            <b>Fundraise.</b> There are plenty of fundraiser ideas that people
            can organize with the help of friends or family. Some ideas include
            a walk-a-thon, a coffee or doughnut sale, or even a benefit concert.
            Another option is to ask friends to abstain from an activity or
            daily habit that costs money for a period of time and then donate
            the saved money.
          </li>
          <li>
            <b>Give money or food. </b>If handing out money makes one
            uncomfortable, consider giving a gift certificate from a restaurant
            or store. One can also choose to buy food and give it directly to
            the individuals who need it.
          </li>
          <li>
            <b>Pay for a room. </b>Help a person or a family get a good night's
            sleep by paying for one or more nights at a hotel.
          </li>
          <li>
            <b>Show kindness.</b> Although a person may be homeless, they are
            still people who deserve respect. An act of kindness may not prevent
            homelessness, but it may brighten a person's day. Give them respect,
            look them in the eye, and treat them the same as any other person
            one may encounter.
          </li>
        </ul>
        <h2 className={styles["Article__sub-heading"]}>A: Advocate</h2>
        <p>
          Learning to advocate for positive change is crucial to ending
          homelessness. This means taking part in activities that will encourage
          government officials at the local and state levels to create policies
          that will help move people from the street back into housing.
        </p>
        <ul>
          <li>
            <b>Join an advocacy coalition</b>. Coalitions are available on a
            state, local, or national level. Volunteer time, contribute
            financially, or do both if possible.
          </li>
          <li>
            <b>Make contact.</b> Visit, email, or send letters to the city,
            state, and federal officials asking for change, and encourage family
            and friends to do the same. One can find contact information online
            or by visiting the library. The more people demand change, the more
            likely it is that officials will pay attention and take action.
          </li>
          <li>
            <b>Attend local meetings.</b> Most areas have town halls or other
            public meetings where they address issues that pertain to the
            community. Being present, however, isn't enough. When attending
            public meetings, stand up and speak up about the need for housing
            programs and ways to prevent homelessness.
          </li>
          <li>
            <b>Use the media.</b> Newspapers, television channels, and other
            news outlets have the unique opportunity to reach a broad audience.
            Contact these outlets to make them aware of issues, such as hate
            crimes against people who are homeless, about which they may not
            already know. One can even write their own article to educate people
            and encourage them to take action, too.
          </li>
          <li>
            <b>Help people vote.</b> People who are currently homeless may not
            know how or if they can vote. Inform people of their right to vote
            and how voting can help implement changes that could help prevent
            homelessness.
          </li>
          <li>
            <b>Become an activist against criminalizing homelessness. </b>Join
            groups such as Housing Not Handcuffs that are attempting to stop
            homeless individuals from being incarcerated because of their lack
            of housing.
          </li>
          <li>
            <b>Take part in National Homeless Persons' Memorial Day.</b>{" "}
            Annually, Dec. 21 is a day for remembering people who have died
            because of homelessness. People can recognize this day in several
            ways, such as coordinating an event or vigil, speaking with people
            who are currently homeless and encouraging them to take part in any
            ceremonies, and making family, friends, and the media aware of the
            significance of the day.
          </li>
        </ul>
        <h2 className={styles["Article__sub-heading"]}>R: Reach Out</h2>
        <p>
          Volunteering one's time is an effective way to not only help people
          who are currently homeless but also help others better understand the
          people who are affected by it. When a person reaches out to help those
          in need, they may choose to work directly with the people they are
          helping or assist in other ways that are just as effective. Because of
          the sheer number of people who are without a place to call home,
          volunteers are typically needed year-round in some capacity.
        </p>
        <ul>
          <li>
            <b>Work at a shelter.</b> This is the most obvious method of
            volunteering one's time. Shelter work may include back office tasks,
            such as answering phones or sorting mail, or jobs like distributing
            clean clothing, serving food to families, or cleaning.
          </li>
          <li>
            <b>Help with housing. </b>Some organizations, like Habitat for
            Humanity, build homes that are used to house people who are
            low-income and may be at risk of becoming homeless. One can reach
            out to these organizations and volunteer their time and skills to
            help fix or build homes for individuals or families.
          </li>
          <li>
            <b>Help people get work.</b> Lack of employment is one reason why
            people cannot afford a place to live. One can use their experience
            and knowledge to help assist people in creating
            r&eacute;sum&eacute;s, filling out applications, and preparing for
            job interviews.
          </li>
          <li>
            <b>Assist children.</b> Being homeless can be hard on children.
            People who are good at interacting with children can volunteer their
            time to tutor or mentor kids, arrange storytime, or organize fun
            events for children in shelters.
          </li>
          <li>
            <b>Encourage others to volunteer.</b> Often, people know others who
            have skills that could benefit those in need. By talking about the
            benefits of volunteering, one may influence others within their
            circle of acquaintance to do the same.
          </li>
        </ul>
        <h2 className={styles["Article__sub-heading"]}>E: Educate</h2>
        <p>
          Often, people's beliefs about homelessness are based on assumptions
          and not facts. In order to effect change, people must be able to
          understand the many factors that may lead a person to homelessness. To
          learn more about homelessness:
        </p>
        <ul>
          <li>
            <b>Conduct online research. </b>Information is readily available on
            the Internet. Visit the websites of organizations that are dedicated
            to ending homelessness, and search government websites for facts and
            statistics.
          </li>
          <li>
            <b>Keep up with the news.</b> Newspapers and televised news
            broadcasts discuss the homelessness crisis routinely. These are
            simple avenues to learn more about homelessness in one's community
            and what is currently being done to slow its growth.
          </li>
          <li>
            <b>Educate children.</b> Parents should take the time to explain why
            adults and children live on the street or in their cars instead of
            in homes. This helps build empathy and can inspire kids to want to
            help.
          </li>
        </ul>
        <h2 className={styles["Article__sub-heading"]}>Further Reading</h2>
        <ul>
          <li>
            <a href="https://ags.betheluniversity.edu/ministry/how-to-help-the-homeless-in-your-community/">
              How to Help the Homelessness in Your Community
            </a>
          </li>
          <li>
            <a href="https://online.simmons.edu/blog/guide-to-supporting-the-homeless/">
              Guide to Supporting the Homeless
            </a>
          </li>
          <li>
            <a href="https://www.stetson.edu/artsci/education/home/media/teacher-help.pdf">
              What Teachers Can Do to Help Homeless Students
            </a>
          </li>
          <li>
            <a href="https://www.coalitionforthehomeless.org/what-should-i-do-if/">
              What Should I Do If ... ?
            </a>
          </li>
          <li>
            <a href="https://ag.umass.edu/sites/ag.umass.edu/files/pdf-doc-ppt/homelessness_awareness.pdf">
              Hunger and Homeless Awareness: Suggested Activities
            </a>
          </li>
          <li>
            <a href="https://www.nonprofitvote.org/voting-and-homelessness/">
              Voting and Homelessness
            </a>
          </li>
          <li>
            <a href="https://housingnothandcuffs.org/get-involved/">
              Housing Not Handcuffs: Get Involved
            </a>
          </li>
          <li>
            <a href="https://www.gofundme.com/c/blog/helping-the-homeless">
              Helping the Homeless: Eight Ways You Can Provide Support
            </a>
          </li>
          <li>
            <a href="https://www.therighttoshower.com/make-difference/7-practical-ways-you-can-help-the-homeless">
              Seven Practical Ways You Can Help People Experiencing Homelessness
            </a>
          </li>
          <li>
            <a href="https://www.californiavolunteers.org/raise-awareness-and-end-the-problem-of-homelessness-in-california/">
              Raise Awareness and End the Problem of Homelessness in{" "}
            </a>
            <a href="https://www.realestateagents.com/real-estate-agents/joshua-tree-ca">
              California
            </a>
          </li>
          <li>
            <a href="https://www.careerandrecovery.org/should-we-give-money-to-the-homeless-people/">
              Should We Give Money to Homeless People?
            </a>
          </li>
          <li>
            <a href="https://www.afil.org/raise-awareness/">
              Raise Awareness: Who Are Homeless Veterans?
            </a>
          </li>
          <li>
            <a href="https://projectonfamilyhomelessness.org/2015/04/24/can-social-media-end-homelessness-a-look-at-new-research/">
              Can Social Media End Homelessness?
            </a>
          </li>
          <li>
            <a href="https://unitedwaymassbay.org/blog/how-to-talk-to-kids-about-homelessness/">
              What Do I Say? How to Talk to Kids About Homelessness
            </a>
          </li>
          <li>
            <a href="https://www.nationalhomeless.org/factsheets/Fact%20Sheet%20and%20LessonPlan-K-2.pdf">
              Homeless Fact Sheet for K-2 Students
            </a>
          </li>
          <li>
            <a href="https://www.dosomething.org/us/articles/things-you-can-do-for-local-youth-experiencing-homelessness">
              Seven Things You Can Do for Youth Experiencing Homelessness
            </a>
          </li>
          <li>
            <a href="http://sites.stedwards.edu/seucombatshomelessness/2021/04/07/homeless-care-packages-dos-and-donts/">
              Homeless Care Package Do's and Don'ts
            </a>
          </li>
          <li>
            <a href="https://www.redmond.gov/DocumentCenter/View/1360/Care-Kit-5-Things-a-Homeless-Person-Could-Use-PDF">
              Care Kit: Five Things a Homeless Person Could Use
            </a>
          </li>
          <li>
            <a href="https://createthegood.aarp.org/volunteer-guides/coat-drive.html">
              How to Hold a Coat Drive
            </a>
          </li>
          <li>
            <a href="https://www.tomsofmaine.com/good-matters/helping-hands/organize-a-sock-drive-for-homeless-neighbors-to-benefit-your-community">
              Organize a Sock Drive for Homeless Neighbors to Benefit Your
              Community
            </a>
          </li>
          <li>
            <a href="https://endhomelessness.org/ending-homelessness/policy/relevant-legislation/?fwp_legislation=homelessness">
              National Alliance to End Homelessness: Relevant Legislation
            </a>
          </li>
          <li>
            <a href="https://www.huffpost.com/entry/how-to-advocate-for-the-h_n_283943">
              11 Ways to Advocate for the Homeless
            </a>
          </li>
          <li>
            <a href="https://ripplekindness.org/10-things-you-can-do-to-help-the-homeless/">
              Ten Things You Can Do to Help the Homeless
            </a>
          </li>
          <li>
            <a href="https://unitedtoendhomelessness.org/myths/">
              Homelessness Myths and Facts
            </a>
          </li>
          <li>
            <a href="https://ofhsoupkitchen.org/end-homelessness">
              Small Ways We Can End Homelessness
            </a>
          </li>
          <li>
            <a href="https://www.nytimes.com/2019/11/27/us/how-to-help-homeless-california.html">
              Know Your Area's Resources: How to Help the Homeless
            </a>
          </li>
          <li>
            <a href="https://www.homelessidproject.org/help-the-homeless/">
              Helping the Homeless: A Practical Guide to Helping Those in Need
            </a>
          </li>
          <li>
            <a href="https://urm.org/about/faqs/helping-people/">
              About Helping People Without{" "}
            </a>
            <a href="https://www.realestateagents.com/real-estate-agents/moraga-ca">
              Homes
            </a>
          </li>
          <li>
            <a href="https://pocketsense.com/help-homeless-people-6503.html">
              How to Help Homeless People
            </a>
          </li>
          <li>
            <a href="https://www.bbc.com/news/uk-england-38221721">
              What Are the Best Ways of Helping Homeless People?
            </a>
          </li>
          <li>
            <a href="https://www.marketwatch.com/story/heres-what-you-can-do-to-help-people-who-are-homeless-this-holiday-season-and-what-not-to-do-2019-12-06">
              How to Give More Than Lip Service to People Who Are Homeless
            </a>
          </li>
        </ul>
      </>
    );
  };

  return (
    <>
      <Head
        title={
          "Helping the Homeless: Ways to Get Involved | RealEstateAgents.com"
        }
        description={
          "We've created a shortlist of ways you can support those currently experiencing homelessness, including actionable items and tasks."
        }
      />
      <article className={styles["Article"]}>
        <div className={styles["Article__container"]}>{content()}</div>
      </article>
      <Script strategy="beforeInteractive" type="application/ld+json">
        {{
          "@context": "https://schema.org",
          "@type": "Article",
          mainEntityOfPage: {
            "@type": "WebPage",
            "@id":
              "https://www.realestateagents.com/articles/helping-the-homeless",
          },
          headline: "Helping the Homeless: Ways to Get Involved",
          author: {
            "@type": "Person",
            name: "Amber Martinez",
            jobTitle: "Education Writer",
            sameAs: [
              "https://www.realestateagents.com/articles/author/amber-martinez",
              "https://www.linkedin.com/in/amber-martinez-7096651a/",
            ],
          },
        }}
      </Script>
    </>
  );
}
